/**
 * Google Places API Service
 * Fetches real-time ratings and reviews for providers
 */

interface PlaceDetails {
  rating: number | null;
  userRatingsTotal: number;
  reviews?: any[];
}

export class GooglePlacesService {
  private apiKey: string;
  private cache: Map<string, { data: PlaceDetails; timestamp: number }>;
  private cacheDuration = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';
    this.cache = new Map();
  }

  /**
   * Search for a place and get its details
   */
  async getPlaceRating(providerName: string, address: string): Promise<PlaceDetails> {
    const cacheKey = `${providerName}-${address}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.data;
    }

    try {
      // Search for the place
      const searchQuery = `${providerName} ${address}`;
      const placeId = await this.findPlaceId(searchQuery);

      if (!placeId) {
        const noData = { rating: null, userRatingsTotal: 0 };
        this.cache.set(cacheKey, { data: noData, timestamp: Date.now() });
        return noData;
      }

      // Get place details
      const details = await this.getPlaceDetails(placeId);

      // Cache the result
      this.cache.set(cacheKey, { data: details, timestamp: Date.now() });

      return details;
    } catch (error) {
      console.error('Error fetching Google Places rating:', error);
      return { rating: null, userRatingsTotal: 0 };
    }
  }

  /**
   * Find place ID using Text Search
   */
  private async findPlaceId(query: string): Promise<string | null> {
    if (!this.apiKey) {
      console.warn('Google Maps API key not configured');
      return null;
    }

    try {
      const url = `https://maps.googleapis.com/maps/api/place/findplacefromtext/json?input=${encodeURIComponent(query)}&inputtype=textquery&fields=place_id&key=${this.apiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK' && data.candidates && data.candidates.length > 0) {
        return data.candidates[0].place_id;
      }

      return null;
    } catch (error) {
      console.error('Error finding place ID:', error);
      return null;
    }
  }

  /**
   * Get detailed place information
   */
  private async getPlaceDetails(placeId: string): Promise<PlaceDetails> {
    try {
      const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=rating,user_ratings_total,reviews&key=${this.apiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK' && data.result) {
        return {
          rating: data.result.rating || null,
          userRatingsTotal: data.result.user_ratings_total || 0,
          reviews: data.result.reviews || [],
        };
      }

      return { rating: null, userRatingsTotal: 0 };
    } catch (error) {
      console.error('Error getting place details:', error);
      return { rating: null, userRatingsTotal: 0 };
    }
  }

  /**
   * Batch fetch ratings for multiple providers
   */
  async batchGetRatings(providers: Array<{ name: string; address: string }>): Promise<Map<string, PlaceDetails>> {
    const results = new Map<string, PlaceDetails>();

    // Process in batches to avoid rate limiting
    const batchSize = 5;
    for (let i = 0; i < providers.length; i += batchSize) {
      const batch = providers.slice(i, i + batchSize);
      const promises = batch.map(p => this.getPlaceRating(p.name, p.address));
      const batchResults = await Promise.all(promises);

      batch.forEach((provider, index) => {
        results.set(provider.name, batchResults[index]);
      });

      // Small delay between batches
      if (i + batchSize < providers.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return results;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Singleton instance
export const googlePlacesService = new GooglePlacesService();
